import React, { useEffect, useMemo, useRef, useState } from 'react';
import { theme as T } from '../../theme';

export interface DateRangeCalendarProps {
  /** Selected start date, YYYY-MM-DD (empty string when unset). */
  fromDate: string;
  /** Selected end date, YYYY-MM-DD (empty string when unset). */
  toDate: string;
  /** Fires with the next (from, to) pair on every day click. */
  onChange: (from: string, to: string) => void;
  /** Latest selectable date, YYYY-MM-DD. Omit to allow any future date. */
  maxDate?: string;
  /** Store-timezone "today", used for the today ring and the default viewed month. */
  todayISO: string;
}

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Scrollable year-list bounds — generous enough to cover any realistic report lookback/forward range.
const YEARS_BACK = 100;
const YEARS_FORWARD = 20;
/**
 * Row height, and therefore the diameter of the endpoint circle and the corner radius of the
 * in-range band. The band cell's WIDTH is always the full grid column (`1fr`) so consecutive
 * in-range days butt up against each other and read as one continuous pill.
 */
const CELL_H = 30;

const pad2 = (n: number) => String(n).padStart(2, '0');
const toISO = (y: number, m: number, d: number) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
const fmtChip = (iso: string) => {
  if (!iso) return 'Select date';
  const [y, m, d] = iso.split('-').map(Number);
  return `${MONTH_LABELS[m - 1].slice(0, 3)} ${d}, ${y}`;
};

/** 6x7 day grid (leading/trailing cells are blank) for the given 0-based month. Pure — safe to memoize. */
function buildMonthGrid(year: number, month: number): { iso: string | null; day: number | null }[] {
  const startWeekday = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: { iso: string | null; day: number | null }[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push({ iso: null, day: null });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ iso: toISO(year, month, d), day: d });
  while (cells.length % 7 !== 0) cells.push({ iso: null, day: null });
  return cells;
}

/**
 * Custom month-grid range calendar — replaces the native `<input type="date">` pair so the
 * From/To picker renders identically across Chrome/Safari/Firefox instead of deferring to each
 * browser's own (visually inconsistent) date-picker UI. Pure client-side date math, no library.
 */
function DateRangeCalendar({ fromDate, toDate, onChange, maxDate, todayISO }: DateRangeCalendarProps) {
  const anchor = fromDate || maxDate || todayISO;
  const [anchorYear, anchorMonthNum] = anchor.split('-').map(Number);
  const [viewYear, setViewYear] = useState(anchorYear);
  const [viewMonth, setViewMonth] = useState(anchorMonthNum - 1); // 0-based
  const [activeField, setActiveField] = useState<'from' | 'to'>('from');
  // 'days' = normal month grid; 'years' = scrollable drill-up view for fast year jumps.
  const [pickerView, setPickerView] = useState<'days' | 'years'>('days');
  const yearListRef = useRef<HTMLDivElement>(null);
  const selectedYearRef = useRef<HTMLButtonElement>(null);

  const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);
  const bandSet = useMemo(() => {
    if (!fromDate || !toDate) return null;
    return { lo: fromDate, hi: toDate };
  }, [fromDate, toDate]);
  const [todayYear] = useMemo(() => todayISO.split('-').map(Number), [todayISO]);
  // Cap the list at maxDate's year when one is set (e.g. reports can't look past today) — no
  // point listing years that are entirely unselectable. Falls back to today+forward when there's
  // no upper bound at all (allowFutureDates callers).
  const maxYear = maxDate ? Number(maxDate.slice(0, 4)) : todayYear + YEARS_FORWARD;
  const years = useMemo(() => {
    const list: number[] = [];
    for (let y = todayYear - YEARS_BACK; y <= maxYear; y++) list.push(y);
    return list;
  }, [todayYear, maxYear]);

  // Center the currently-viewed year in the scroll container the moment the list opens.
  useEffect(() => {
    if (pickerView === 'years') selectedYearRef.current?.scrollIntoView({ block: 'center' });
  }, [pickerView]);

  const goPrevMonth = () => {
    if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11); } else setViewMonth((m) => m - 1);
  };
  const goNextMonth = () => {
    if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0); } else setViewMonth((m) => m + 1);
  };
  const goToday = () => {
    const [y, m] = todayISO.split('-').map(Number);
    setViewYear(y);
    setViewMonth(m - 1);
    setPickerView('days');
  };

  const selectYear = (y: number) => {
    setViewYear(y);
    setPickerView('days');
  };

  /** Targeting a field also re-centers the calendar on that field's own date — From/To can be
   *  years apart, so switching should jump the view there instead of leaving it wherever it was. */
  /** Re-center the calendar on a given date's month — used any time the active field's own
   *  date should now be on screen, whether that field was targeted by a chip click or by the
   *  automatic From→To advance below. */
  const jumpViewTo = (iso: string) => {
    const [y, m] = iso.split('-').map(Number);
    setViewYear(y);
    setViewMonth(m - 1);
  };

  const focusField = (field: 'from' | 'to') => {
    setActiveField(field);
    const val = field === 'from' ? fromDate : toDate;
    if (val) jumpViewTo(val);
    setPickerView('days');
  };

  const isDisabled = (iso: string) => !!maxDate && iso > maxDate;

  const handleDayClick = (iso: string) => {
    if (isDisabled(iso)) return;
    if (activeField === 'from') {
      // A single tap is a complete, valid single-day selection: From AND To both land on
      // the tapped day, so Apply never sits behind a second click (GLOBAL-DP-1). The user
      // widens the range by tapping a second day, which the "to" branch below handles.
      onChange(iso, iso);
      setActiveField('to');
    } else if (fromDate && iso < fromDate) {
      // Picked an end date before the start — restart the range from that day and stay on
      // "To", so the next tap closes the range forwards rather than silently inverting it.
      onChange(iso, iso);
    } else {
      onChange(fromDate || iso, iso);
    }
  };

  const inBand = (iso: string) => !!bandSet && iso >= bandSet.lo && iso <= bandSet.hi;

  return (
    <div style={{ fontFamily: T.f }}>
      {/* From / To chips — click to target the next day click at that field */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {(['from', 'to'] as const).map((field) => {
          const active = activeField === field;
          const val = field === 'from' ? fromDate : toDate;
          return (
            <button
              key={field}
              type="button"
              onClick={() => focusField(field)}
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: 'left',
                padding: '6px 8px',
                borderRadius: 6,
                border: `1px solid ${active ? T.accent : T.border}`,
                background: active ? T.accentSoft : T.surface,
                cursor: 'pointer',
                fontFamily: T.f,
              }}
            >
              <div style={{ fontSize: 9.5, fontWeight: 600, color: active ? T.accent : T.t3, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                {field === 'from' ? 'From' : 'To'}
              </div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: val ? T.t1 : T.t3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {fmtChip(val)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Month/year navigation — click the label to drill up into a fast scrollable year picker */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => setPickerView((v) => (v === 'days' ? 'years' : 'days'))}
          style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 12, fontWeight: 700, color: T.t1, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: T.f, padding: '2px 4px', borderRadius: 5 }}
        >
          {pickerView === 'days' ? `${MONTH_LABELS[viewMonth]} ${viewYear}` : 'Choose year'}
          <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t3 }}>{pickerView === 'days' ? 'expand_more' : 'expand_less'}</span>
        </button>
        {pickerView === 'days' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <button
              type="button"
              onClick={goToday}
              aria-label="Jump to today"
              title="Jump to today"
              style={{ width: 22, height: 22, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 15, color: T.t3 }}>today</span>
            </button>
            <button
              type="button"
              onClick={goPrevMonth}
              aria-label="Previous month"
              style={{ width: 24, height: 24, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>chevron_left</span>
            </button>
            <button
              type="button"
              onClick={goNextMonth}
              aria-label="Next month"
              style={{ width: 24, height: 24, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>chevron_right</span>
            </button>
          </div>
        )}
      </div>

      {pickerView === 'years' ? (
        /* Scrollable year list — 3-column grid, auto-centered on the currently viewed year. */
        <div ref={yearListRef} style={{ maxHeight: 220, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, paddingRight: 2 }}>
          {years.map((y) => {
            const selected = y === viewYear;
            return (
              <button
                key={y}
                ref={selected ? selectedYearRef : undefined}
                type="button"
                onClick={() => selectYear(y)}
                style={{
                  padding: '10px 0',
                  borderRadius: 8,
                  border: 'none',
                  background: selected ? T.hover : 'transparent',
                  color: T.t1,
                  fontWeight: selected ? 700 : 500,
                  fontSize: 13,
                  fontFamily: T.f,
                  cursor: 'pointer',
                }}
              >
                {y}
              </button>
            );
          })}
        </div>
      ) : (
        <>
          {/* Weekday header — cells fill their grid column so the labels sit centred over
              the day cells below, which are also column-width. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 2 }}>
            {WEEKDAY_LABELS.map((w) => (
              <div key={w} style={{ width: '100%', height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontWeight: 600, color: T.t3 }}>
                {w}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {grid.map((cell, idx) => {
              if (!cell.iso) return <div key={idx} style={{ width: '100%', height: CELL_H }} />;
              const iso = cell.iso;
              const isFrom = iso === fromDate;
              const isTo = iso === toDate;
              const isEndpoint = isFrom || isTo;
              const isInBand = inBand(iso);
              const disabled = isDisabled(iso);
              const isToday = iso === todayISO;

              const col = idx % 7;
              const leftIso = col > 0 ? grid[idx - 1]?.iso : null;
              const rightIso = col < 6 ? grid[idx + 1]?.iso : null;
              const roundLeft = !leftIso || !inBand(leftIso);
              const roundRight = !rightIso || !inBand(rightIso);

              return (
                /* The band cell must span the FULL grid column, not a fixed 30px: the grid
                   columns are `1fr` (~38px in the desktop popover, wider on the mobile
                   sheet), so a fixed-width cell leaves an unpainted gutter either side and
                   the selected range renders as detached blocks instead of one pill. */
                <div
                  key={idx}
                  style={{
                    width: '100%',
                    height: CELL_H,
                    background: isInBand ? T.accentSoft : 'transparent',
                    borderTopLeftRadius: isInBand && roundLeft ? CELL_H / 2 : 0,
                    borderBottomLeftRadius: isInBand && roundLeft ? CELL_H / 2 : 0,
                    borderTopRightRadius: isInBand && roundRight ? CELL_H / 2 : 0,
                    borderBottomRightRadius: isInBand && roundRight ? CELL_H / 2 : 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => handleDayClick(iso)}
                    aria-label={iso}
                    style={{
                      width: CELL_H,
                      height: CELL_H,
                      flexShrink: 0,
                      borderRadius: '50%',
                      border: isToday && !isEndpoint ? `1px solid ${T.accent}` : 'none',
                      background: isEndpoint ? T.accent : 'transparent',
                      color: disabled ? T.t3 : isEndpoint ? '#fff' : T.t1,
                      fontWeight: isEndpoint ? 700 : 500,
                      fontSize: 11.5,
                      fontFamily: T.f,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.4 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {cell.day}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Only re-renders when the range/bounds actually change — the popover already mounts this
// component lazily (only while open), so this keeps re-renders to a minimum on top of that.
export default React.memo(DateRangeCalendar);
